import { callMethod } from '@ipfabric/utils/fp/function';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { pipe } from 'fp-ts/lib/function';

export interface SingletonController {
  abort: () => void;
  nextSignal: () => AbortSignal;
}

/**
 * Utility for creating singleton cancellable queries
 *
 * Each call to nextSignal() provides new AbortSignal and cancel previously existing one, if any.
 * Useful for requests where firing next request makes previous irrelevant (cancel loading
 * data when different data were requested) and where this can't be achieved with useEffect's destructor.
 *
 * @example
 * // somewhere in big component's global hook
 * const singletonController = useMemo(() => createSingletonController(), []);
 * useEffect(() => () => {
 *   singletonController.abort();
 * });
 *
 * // some command
 * const loadItems: SomeCommandFactory = cmdContext => async command => {
 *   const result = await cmdContext.api.abortableRequest({
 *     url: '...',
 *     abortSignal: cmdContext.itemsSingletonController.nextSignal(),
 *   });
 *   if (!result.aborted) {
 *     cmdContext.updateState(draft => { draft.data = result.data; });
 *   }
 * };
 */
export const createSingletonController = (): SingletonController => {
  let controller: AbortController | null = null;
  const abort = () => controller?.abort();
  const nextSignal = () => {
    controller?.abort();
    controller = new AbortController();
    return controller!.signal;
  };
  return {
    abort,
    nextSignal,
  };
};

export interface SingletonControllerCollection<TKey extends string> {
  abort: (key: TKey) => void;
  abortAll: () => void;
  nextSignal: (key: TKey) => AbortSignal;
}

/**
 * Collection of singleton controllers that can be easily terminated all at once
 *
 * @example
 * type MySingletonController = 'loadFoos' | 'loadBars';
 * // somewhere in big component's global hook
 * const singletonControllers = useMemo(() => createSingletonControllerCollection<MySingletonController>(), []);
 * useEffect(() => () => {
 *   singletonControllers.abortAll();
 * });
 *
 * // some command
 * const loadFoos: MyCommandFactory = cmdContext => async command => {
 *   const result = await cmdContext.api.abortableRequest({
 *     url: '...',
 *     abortSignal: cmdContext.singletonControllers.nextSignal('loadFoos'),
 *   });
 *   // ...
 * };
 */
export const createSingletonControllerCollection = <
  TKey extends string,
>(): SingletonControllerCollection<TKey> => {
  const collection = new Map<TKey, SingletonController>();
  const abort = (key: TKey) => collection.get(key)?.abort();
  const abortAll = () => collection.forEach(callMethod('abort'));
  const nextSignal = (key: TKey) =>
    pipe(
      collection,
      plainMap.getOrCreate(key, createSingletonController),
      callMethod('nextSignal'),
    );
  return {
    abort,
    abortAll,
    nextSignal,
  };
};
